import { useState } from "react";
import { Mail, Clock, Truck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const INFO = [
  { icon: Mail, label: "Email", value: "tanonshop@gmail.com" },
  { icon: Clock, label: "Hours", value: "Mon–Fri · 9am–5pm" },
  { icon: Truck, label: "Shipping", value: "Nationwide" },
  { icon: MessageSquare, label: "Response", value: "Within 1–2 business days" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" description="Get in touch with Tanonshop about custom cushion cover projects, quotes and questions." />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-16 lg:pt-28">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Say hello</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03]">
              Let's talk about your project.
            </h1>
            <p className="mt-6 text-lg text-teal-anchor/70 leading-relaxed font-light">
              Questions about fabrics, timelines or measuring? Send a note and we'll help. For a full
              price estimate, use the custom order form so we have your measurements on hand.
            </p>
            <div className="mt-12 grid gap-8 sm:grid-cols-2">
              {INFO.map((it) => (
                <div key={it.label} className="border-t border-teal-anchor/15 pt-5">
                  <it.icon className="text-teal-primary" size={22} strokeWidth={1.5} />
                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-teal-anchor/50">{it.label}</p>
                  <p className="mt-1 text-teal-anchor">{it.value}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="border border-teal-anchor/12 bg-white p-8 lg:p-10">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.15em]">Name *</Label>
                  <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20" />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-xs uppercase tracking-[0.15em]">Email *</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20" />
                </div>
                <div>
                  <Label htmlFor="c-subject" className="text-xs uppercase tracking-[0.15em]">Subject</Label>
                  <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20" />
                </div>
                <div>
                  <Label htmlFor="c-msg" className="text-xs uppercase tracking-[0.15em]">Message *</Label>
                  <Textarea id="c-msg" rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20" />
                </div>
                <button type="submit" data-testid="contact-submit" disabled={submitting} className="w-full rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px disabled:opacity-60">
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
